import { reactive } from "vue";
import {
  FilterCondition,
  MarketDisplayItemContract,
  UnMatchedDeal,
} from "@/models/marketData";
import useConsoleLogger from "./useConsoleLogger";

const CType = ["futures", "options", "splits", "spreads", "deltas"] as const;
export type ContractType = (typeof CType)[number];
interface Filters extends Record<ContractType, FilterCondition[]> {
  futures: FilterCondition[];
  options: FilterCondition[];
  splits: FilterCondition[];
  spreads: FilterCondition[];
  deltas: FilterCondition[];
}

interface CompiledCondition {
  fieldPaths: string[][];
  operator: FilterCondition["operator"];
  value: FilterCondition["value"];
}

function compileCondition(condition: FilterCondition): CompiledCondition {
  return {
    fieldPaths: condition.field.split("/").map((f) => f.split(".")),
    operator: condition.operator,
    value: condition.value,
  };
}

function applyCompiledConditions(item: any, conditions: CompiledCondition[]): boolean {
  return conditions.every((condition) => {
    const { operator, value } = condition;

    for (const fieldParts of condition.fieldPaths) {
      const fieldValue = fieldParts.reduce((o: any, i: string) => o?.[i], item);

      switch (operator) {
        case "==":
          return fieldValue == value;

        case "!==":
          return fieldValue !== value;

        case ">":
          return fieldValue > value;

        case "<":
          return fieldValue < value;

        case ">=":
          return fieldValue >= value;

        case "<=":
          return fieldValue <= value;

        case "contains":
          const val = value || "";
          if (
            typeof fieldValue === "string" &&
            fieldValue.toLowerCase().includes((val as string).toLowerCase())
          ) {
            return true;
          }
          break;

        default:
          useConsoleLogger.log("Op full default: ", fieldValue, operator, value);
          return false;
      }
    }

    return false;
  });
}

// Module-level singleton — all 25 consumers share one instance; filters data is static
const _filters = reactive<Filters>({
  futures: [
    { field: "contractDisplay.flag", value: "F", operator: "==" },
    { field: "contractDisplay.strike", value: 0, operator: "==" },
    { field: "contractDisplay.CONTRACT_TYPE", value: 1, operator: "==" },
  ],
  options: [
    { field: "contractDisplay.flag", value: "F", operator: "!==" },
    { field: "contractDisplay.CONTRACT_TYPE", value: 2, operator: "==" },
    { field: "contractDisplay.strike", value: 0, operator: "!==" },
  ],
  splits: [
    { field: "contractDisplay.CONTRACT_TYPE", value: 4, operator: "==" },
  ],
  spreads: [
    { field: "contractDisplay.CONTRACT_TYPE", value: 3, operator: "==" },
  ],
  deltas: [
    { field: "contractDisplay.flag", value: "F", operator: "!==" },
    { field: "contractDisplay.strike", value: "0", operator: "!==" },
    { field: "contractDisplay.CONTRACT_TYPE", value: 5, operator: "==" },
  ],
});

// Pre-compile all known filter arrays once at module load — avoids field.split() on every WS message
const _compiledMap = new WeakMap<FilterCondition[], CompiledCondition[]>();
for (const key of CType) {
  _compiledMap.set(_filters[key], _filters[key].map(compileCondition));
}

function _applyConditions(item: any, conditions: FilterCondition[]): boolean {
  const compiled = _compiledMap.get(conditions) ?? conditions.map(compileCondition);
  return applyCompiledConditions(item, compiled);
}

function _determineContractType(
  item?: MarketDisplayItemContract | UnMatchedDeal
): ContractType | undefined {
  for (const contractType of CType) {
    const compiled = _compiledMap.get(_filters[contractType])!;
    if (applyCompiledConditions(item, compiled)) {
      return contractType;
    }
  }
  return undefined;
}

export default function useContractTypeFilters() {
  return {
    filters: _filters,
    determineContractType: _determineContractType,
    applyConditions: _applyConditions,
  };
}
